import React, { useState, useMemo } from 'react';
import { Row, Col, Button, Alert, Card, Table, Badge, Spinner, Modal } from 'react-bootstrap';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowLeft,
  faCheckCircle,
  faExclamationTriangle,
  faTimesCircle,
  faSpinner,
  faSave,
  faSync,
  faWandMagicSparkles,
  faExternalLinkAlt,
  faSignature,
  faQuestionCircle,
} from '@fortawesome/free-solid-svg-icons';
import { toast } from 'react-toastify';

import PrivateLayout from '../layouts/PrivateLayout';
import Panel from '../common/Panel';

// ============================================================================
// STANDARD EXAMINER/CERTIFICATION FIELD IDS
// These are the canonical field names that should be used across ALL DBQ forms
// for the examiner certification section.
// ============================================================================
export const STANDARD_EXAMINER_FIELDS = {
  examiner_signature: {
    label: "Examiner's Signature",
    type: 'signature',
    aliases: ['signature', 'examiner_sig', 'provider_signature'],
  },
  examiner_printed_name_and_title: {
    label: "Examiner's Printed Name and Title",
    type: 'text',
    aliases: ['examiner_name_title', 'examiner_name_and_title', 'printed_name_and_title', 'name_and_title'],
  },
  examiner_area_of_practice: {
    label: "Examiner's Area of Practice/Specialty",
    type: 'text',
    aliases: ['examiner_specialty', 'area_of_practice', 'specialty', 'examiner_area_of_practice_specialty'],
  },
  examiner_date_signed: {
    label: 'Date Signed',
    type: 'date',
    aliases: ['date_signed', 'signature_date', 'examiner_date'],
  },
  examiner_phone_fax: {
    label: 'Phone/FAX Numbers',
    type: 'text',
    aliases: ['phone_fax', 'phone_fax_numbers', 'examiner_phone', 'phone_fax_number'],
  },
  examiner_npi_number: {
    label: 'National Provider Identifier (NPI) Number',
    type: 'text',
    aliases: ['examiner_npi', 'npi', 'npi_number', 'national_provider_identifier'],
  },
  examiner_medical_license_number_and_state: {
    label: 'Medical License Number and State',
    type: 'text',
    aliases: ['examiner_license', 'license_number_and_state', 'medical_license', 'examiner_license_number'],
  },
  examiner_address: {
    label: "Examiner's Address",
    type: 'text',
    aliases: ['address', 'examiner_addr', 'provider_address'],
  },
};

// Build a reverse lookup from aliases to standard field names
const buildAliasLookup = () => {
  const lookup = {};
  Object.entries(STANDARD_EXAMINER_FIELDS).forEach(([standardId, config]) => {
    lookup[standardId.toLowerCase()] = standardId;
    config.aliases.forEach((alias) => {
      lookup[alias.toLowerCase()] = standardId;
    });
  });
  return lookup;
};

const ALIAS_LOOKUP = buildAliasLookup();

/**
 * Try to match a field ID to a standard examiner field
 */
const matchToStandardField = (fieldId) => {
  if (!fieldId) return null;
  const normalized = fieldId.toLowerCase().replace(/^derived_\d*_?/, '');
  
  // Direct match
  if (ALIAS_LOOKUP[normalized]) {
    return ALIAS_LOOKUP[normalized];
  }
  
  // Fuzzy match - check if the fieldId contains key parts
  for (const [standardId, config] of Object.entries(STANDARD_EXAMINER_FIELDS)) {
    const standardParts = standardId.split('_');
    const matchingParts = standardParts.filter((part) => normalized.includes(part));
    if (matchingParts.length >= 2) {
      return standardId;
    }
    
    // Check aliases
    for (const alias of config.aliases) {
      const aliasParts = alias.split('_');
      const aliasMatchingParts = aliasParts.filter((part) => normalized.includes(part));
      if (aliasMatchingParts.length >= Math.min(2, aliasParts.length)) {
        return standardId;
      }
    }
  }
  
  return null;
};

/**
 * Extract template field names from docusealTemplate
 */
const extractTemplateFields = (docusealTemplate) => {
  if (!docusealTemplate?.fields) return [];
  return docusealTemplate.fields;
};

/**
 * Generate expected DocuSeal field IDs from formJson for examiner fields only
 */
const extractFormExaminerFields = (formJson) => {
  const examinerFields = {};
  
  const walkElements = (elements) => {
    if (!Array.isArray(elements)) return;
    for (const el of elements) {
      if (!el) continue;
      if (el.type === 'field' && el.id) {
        const standardMatch = matchToStandardField(el.id);
        if (standardMatch) {
          examinerFields[standardMatch] = {
            formFieldId: el.id,
            label: el.label,
          };
        }
      }
      if (el.elements) walkElements(el.elements);
      if (el.options) {
        for (const opt of el.options) {
          if (opt?.child_elements) walkElements(opt.child_elements);
        }
      }
    }
  };
  
  if (formJson?.sections) {
    for (const section of formJson.sections) {
      if (section?.elements) walkElements(section.elements);
    }
  }
  
  return examinerFields;
};

const DbqTemplateDebug = () => {
  const { dbqFormId } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [examinerChanges, setExaminerChanges] = useState([]);
  const [aiSuggestions, setAiSuggestions] = useState(null);
  
  // Fetch the DBQ form data
  const { data: formData, isLoading, error, refetch } = useQuery({
    queryKey: ['dbq-form-debug', dbqFormId],
    queryFn: async () => {
      const response = await axios.get(`/dbq/forms/${dbqFormId}`);
      return response.data;
    },
    enabled: !!dbqFormId,
  });
  
  const form = formData?.form;
  const formJson = useMemo(() => {
    if (!form?.formJson) return null;
    return typeof form.formJson === 'string' ? JSON.parse(form.formJson) : form.formJson;
  }, [form?.formJson]);
  
  const docusealTemplate = useMemo(() => {
    if (!form?.docusealTemplate) return null;
    return typeof form.docusealTemplate === 'string' 
      ? JSON.parse(form.docusealTemplate) 
      : form.docusealTemplate;
  }, [form?.docusealTemplate]);
  
  // Analyze examiner fields
  const examinerAnalysis = useMemo(() => {
    if (!docusealTemplate) return { fields: [], needsFixes: false, fixes: [] };
    
    const templateFields = extractTemplateFields(docusealTemplate);
    const formExaminerFields = formJson ? extractFormExaminerFields(formJson) : {};
    
    const fields = [];
    const fixes = [];
    
    Object.entries(STANDARD_EXAMINER_FIELDS).forEach(([standardId, config]) => {
      // Find in template (exact match, alias, or derived)
      const exactMatch = templateFields.find((f) => f.name === standardId);
      const aliasMatch = !exactMatch && templateFields.find((f) => config.aliases.includes(f.name));
      const derivedMatch = !exactMatch && !aliasMatch && templateFields.find((f) => 
        f.name?.startsWith('derived_') && matchToStandardField(f.name) === standardId
      );
      
      const templateField = exactMatch || aliasMatch || derivedMatch;
      const formField = formExaminerFields[standardId];
      
      const status = exactMatch 
        ? 'correct' 
        : (aliasMatch || derivedMatch) 
          ? 'needs_rename' 
          : 'missing';
      
      fields.push({
        standardId,
        label: config.label,
        type: config.type,
        templateFieldName: templateField?.name || null,
        templateFieldType: templateField?.type || null,
        formFieldId: formField?.formFieldId || null,
        formFieldLabel: formField?.label || null,
        status,
        matchType: exactMatch ? 'exact' : aliasMatch ? 'alias' : derivedMatch ? 'derived' : null,
      });
      
      // Build fix if needed
      if (status === 'needs_rename' && templateField) {
        fixes.push({
          type: 'rename_template_field',
          from: templateField.name,
          to: standardId,
          label: config.label,
        });
      }
    });
    
    return {
      fields,
      needsFixes: fixes.length > 0,
      fixes,
    };
  }, [docusealTemplate, formJson]);
  
  // Analyze derived fields (non-examiner)
  const derivedFieldsAnalysis = useMemo(() => {
    if (!docusealTemplate) return { fields: [], count: 0 };
    
    const templateFields = extractTemplateFields(docusealTemplate);
    const allTemplateFieldNames = templateFields.map((f) => f.name);
    
    // Helper to get the primary position of a field (first area)
    const getFieldPosition = (field) => {
      const area = field?.areas?.[0];
      if (!area) return null;
      return {
        page: area.page || 0,
        x: area.x || 0,
        y: area.y || 0,
      };
    };
    
    // Helper to calculate distance between two positions (same page only)
    const calculateDistance = (pos1, pos2) => {
      if (!pos1 || !pos2) return Infinity;
      if (pos1.page !== pos2.page) return Infinity; // Different pages = infinite distance
      // Euclidean distance using normalized coordinates
      return Math.sqrt(Math.pow(pos1.x - pos2.x, 2) + Math.pow(pos1.y - pos2.y, 2));
    };
    
    // Get derived fields that are NOT examiner fields
    const derivedFields = templateFields
      .filter((f) => f.name?.startsWith('derived_'))
      .filter((f) => !matchToStandardField(f.name)) // Exclude examiner fields
      .map((f) => {
        const fieldPos = getFieldPosition(f);
        
        // Find spatially nearby fields based on position
        const nearbyFields = [];
        if (fieldPos) {
          // Calculate distances to all non-derived fields
          const fieldsWithDistance = templateFields
            .filter((tf) => tf.name !== f.name && !tf.name?.startsWith('derived_'))
            .map((tf) => ({
              name: tf.name,
              position: getFieldPosition(tf),
              distance: calculateDistance(fieldPos, getFieldPosition(tf)),
            }))
            .filter((tf) => tf.distance !== Infinity) // Same page only
            .sort((a, b) => a.distance - b.distance)
            .slice(0, 5); // Get 5 closest fields
          
          nearbyFields.push(...fieldsWithDistance.map((tf) => tf.name));
        }
        
        return {
          name: f.name,
          type: f.type,
          position: fieldPos,
          nearbyFields,
        };
      });
    
    return {
      fields: derivedFields,
      count: derivedFields.length,
      allTemplateFieldNames,
      templateFields, // Include full field data for AI context
    };
  }, [docusealTemplate]);
  
  // Mutation for refreshing template from DocuSeal
  const refreshTemplateMutation = useMutation({
    mutationFn: async () => {
      const response = await axios.post(`/dbq/forms/${dbqFormId}/refresh-template`);
      return response.data;
    },
    onSuccess: () => {
      toast.success('Template refreshed from DocuSeal');
      queryClient.invalidateQueries(['dbq-form-debug', dbqFormId]);
      refetch();
    },
    onError: (err) => {
      toast.error(err?.response?.data?.msg || 'Failed to refresh template');
    },
  });
  
  // Mutation for fixing examiner fields
  const fixExaminerFieldsMutation = useMutation({
    mutationFn: async (changes) => {
      const response = await axios.post(`/dbq/forms/${dbqFormId}/fix-examiner-fields`, {
        changes,
      });
      return response.data;
    },
    onSuccess: () => {
      toast.success('Examiner fields updated successfully');
      setShowConfirmModal(false);
      queryClient.invalidateQueries(['dbq-form-debug', dbqFormId]);
      refetch();
    },
    onError: (err) => {
      toast.error(err?.response?.data?.msg || 'Failed to update examiner fields');
    },
  });
  
  // Mutation for AI suggestions on derived fields
  const getAiSuggestionsMutation = useMutation({
    mutationFn: async () => {
      // Include position data and nearby fields for better AI context
      const response = await axios.post(`/dbq/forms/${dbqFormId}/suggest-derived-fixes`, {
        derivedFields: derivedFieldsAnalysis.fields,
        allTemplateFieldNames: derivedFieldsAnalysis.allTemplateFieldNames,
        // Pass template fields with positions for AI to use spatial context
        templateFieldsWithPositions: derivedFieldsAnalysis.templateFields?.map((f) => ({
          name: f.name,
          type: f.type,
          position: f.areas?.[0] ? {
            page: f.areas[0].page,
            x: f.areas[0].x,
            y: f.areas[0].y,
          } : null,
        })),
      });
      return response.data;
    },
    onSuccess: (data) => {
      setAiSuggestions(data.suggestions);
      toast.success('AI suggestions generated');
    },
    onError: (err) => {
      toast.error(err?.response?.data?.msg || 'Failed to generate suggestions');
    },
  });
  
  const handleFixExaminerFields = () => {
    setExaminerChanges(examinerAnalysis.fixes);
    setShowConfirmModal(true);
  };
  
  const handleConfirmFix = () => {
    fixExaminerFieldsMutation.mutate(examinerChanges);
  };
  
  if (isLoading) {
    return (
      <PrivateLayout>
        <Panel>
          <div className="text-center py-5">
            <FontAwesomeIcon icon={faSpinner} spin size="2x" />
            <p className="mt-3 text-muted">Loading form data...</p>
          </div>
        </Panel>
      </PrivateLayout>
    );
  }
  
  if (error) {
    return (
      <PrivateLayout>
        <Panel>
          <Alert variant="danger">
            <h5>Error loading form</h5>
            <p className="mb-0">{error?.response?.data?.msg || error?.message || 'Unknown error'}</p>
          </Alert>
          <Button variant="secondary" onClick={() => navigate('/dbq')}>
            <FontAwesomeIcon icon={faArrowLeft} className="me-2" />
            Back to DBQ Library
          </Button>
        </Panel>
      </PrivateLayout>
    );
  }
  
  if (!form) {
    return (
      <PrivateLayout>
        <Panel>
          <Alert variant="warning">Form not found</Alert>
          <Button variant="secondary" onClick={() => navigate('/dbq')}>
            <FontAwesomeIcon icon={faArrowLeft} className="me-2" />
            Back to DBQ Library
          </Button>
        </Panel>
      </PrivateLayout>
    );
  }
  
  const hasNoTemplate = !docusealTemplate;
  
  return (
    <PrivateLayout>
      <Panel className="pb-3 mb-3">
        {/* Header */}
        <Row className="mb-4">
          <Col>
            <div className="d-flex align-items-center gap-3">
              <Button 
                variant="outline-secondary" 
                size="sm"
                onClick={() => navigate('/dbq')}
              >
                <FontAwesomeIcon icon={faArrowLeft} className="me-2" />
                Back
              </Button>
              <div>
                <h4 className="mb-0">DocuSeal Template Debug</h4>
                <div className="text-muted">
                  {form.title} <code className="ms-2 small">{form.formId}</code>
                </div>
              </div>
            </div>
          </Col>
          <Col xs="auto">
            <Link to={`/dbq/${dbqFormId}`} className="btn btn-outline-primary btn-sm">
              <FontAwesomeIcon icon={faExternalLinkAlt} className="me-2" />
              View Form
            </Link>
          </Col>
        </Row>
        
        {hasNoTemplate && (
          <Alert variant="warning">
            <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
            <strong>No DocuSeal template data stored.</strong> Run the backfill script or recreate the template to populate this data.
          </Alert>
        )}
        
        {/* ================================================================== */}
        {/* SECTION 1: Examiner's Signature Info */}
        {/* ================================================================== */}
        <Card className="mb-4">
          <Card.Header className="d-flex justify-content-between align-items-center">
            <div>
              <FontAwesomeIcon icon={faSignature} className="me-2" />
              <strong>Examiner's Signature Info</strong>
              {examinerAnalysis.needsFixes ? (
                <Badge bg="warning" className="ms-2">
                  {examinerAnalysis.fixes.length} fix{examinerAnalysis.fixes.length !== 1 ? 'es' : ''} needed
                </Badge>
              ) : (
                <Badge bg="success" className="ms-2">All correct</Badge>
              )}
            </div>
            {examinerAnalysis.needsFixes && (
              <Button 
                variant="primary" 
                size="sm"
                onClick={handleFixExaminerFields}
                disabled={fixExaminerFieldsMutation.isPending}
              >
                {fixExaminerFieldsMutation.isPending ? (
                  <FontAwesomeIcon icon={faSpinner} spin className="me-2" />
                ) : (
                  <FontAwesomeIcon icon={faSave} className="me-2" />
                )}
                Fix All Examiner Fields
              </Button>
            )}
          </Card.Header>
          <Card.Body className="p-0">
            <Table responsive className="mb-0">
              <thead>
                <tr>
                  <th>Standard Field</th>
                  <th>In DocuSeal Template</th>
                  <th>In Form (formJson)</th>
                  <th className="text-center">Status</th>
                </tr>
              </thead>
              <tbody>
                {examinerAnalysis.fields.map((field, idx) => (
                  <tr key={idx}>
                    <td>
                      <code className="text-success">{field.standardId}</code>
                      <div className="text-muted small">{field.label}</div>
                    </td>
                    <td>
                      {field.templateFieldName ? (
                        <>
                          <code className={
                            field.status === 'correct' 
                              ? 'text-success' 
                              : field.templateFieldName.startsWith('derived_')
                                ? 'text-warning'
                                : 'text-info'
                          }>
                            {field.templateFieldName}
                          </code>
                          {field.matchType === 'alias' && (
                            <Badge bg="info" className="ms-2 small">alias</Badge>
                          )}
                          {field.matchType === 'derived' && (
                            <Badge bg="warning" className="ms-2 small">derived</Badge>
                          )}
                        </>
                      ) : (
                        <span className="text-muted">—</span>
                      )}
                    </td>
                    <td>
                      {field.formFieldId ? (
                        <>
                          <code>{field.formFieldId}</code>
                          {field.formFieldLabel && (
                            <div className="text-muted small">{field.formFieldLabel}</div>
                          )}
                        </>
                      ) : (
                        <span className="text-muted">—</span>
                      )}
                    </td>
                    <td className="text-center">
                      {field.status === 'correct' ? (
                        <FontAwesomeIcon icon={faCheckCircle} className="text-success" title="Correct" />
                      ) : field.status === 'needs_rename' ? (
                        <FontAwesomeIcon icon={faExclamationTriangle} className="text-warning" title="Needs rename" />
                      ) : (
                        <FontAwesomeIcon icon={faTimesCircle} className="text-danger" title="Missing" />
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </Card.Body>
        </Card>
        
        {/* ================================================================== */}
        {/* SECTION 2: Derived Fields */}
        {/* ================================================================== */}
        <Card>
          <Card.Header className="d-flex justify-content-between align-items-center">
            <div>
              <FontAwesomeIcon icon={faQuestionCircle} className="me-2" />
              <strong>Derived Fields (Non-Examiner)</strong>
              <Badge 
                bg={derivedFieldsAnalysis.count > 0 ? 'warning' : 'success'} 
                className="ms-2"
              >
                {derivedFieldsAnalysis.count}
              </Badge>
            </div>
            <div className="d-flex gap-2">
              <Button 
                variant="outline-secondary" 
                size="sm"
                onClick={() => refreshTemplateMutation.mutate()}
                disabled={refreshTemplateMutation.isPending}
              >
                {refreshTemplateMutation.isPending ? (
                  <FontAwesomeIcon icon={faSpinner} spin className="me-2" />
                ) : (
                  <FontAwesomeIcon icon={faSync} className="me-2" />
                )}
                Refresh
              </Button>
              <Button 
                variant="outline-primary" 
                size="sm"
                onClick={() => getAiSuggestionsMutation.mutate()}
                disabled={getAiSuggestionsMutation.isPending || derivedFieldsAnalysis.count === 0}
              >
                {getAiSuggestionsMutation.isPending ? (
                  <FontAwesomeIcon icon={faSpinner} spin className="me-2" />
                ) : (
                  <FontAwesomeIcon icon={faWandMagicSparkles} className="me-2" />
                )}
                Get AI Suggestions
              </Button>
            </div>
          </Card.Header>
          <Card.Body>
            {derivedFieldsAnalysis.count === 0 ? (
              <Alert variant="success" className="mb-0">
                <FontAwesomeIcon icon={faCheckCircle} className="me-2" />
                No derived fields found. All template fields are properly named.
              </Alert>
            ) : (
              <>
                <p className="text-muted small mb-3">
                  These <code>derived_</code> fields were auto-generated during PDF analysis and need proper names.
                  Use the AI suggestions to help identify what they should be renamed to, then make the changes
                  manually in the DocuSeal interface.
                </p>
                
                <Table responsive size="sm">
                  <thead>
                    <tr>
                      <th>Derived Field Name</th>
                      <th>Type</th>
                      <th>Position</th>
                      <th>Nearby Fields (by position)</th>
                      {aiSuggestions && <th>AI Suggestion</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {derivedFieldsAnalysis.fields.map((field, idx) => {
                      const suggestion = aiSuggestions?.find((s) => s.fieldName === field.name);
                      return (
                        <tr key={idx}>
                          <td>
                            <code className="text-warning">{field.name}</code>
                          </td>
                          <td>
                            <Badge bg="secondary">{field.type}</Badge>
                          </td>
                          <td className="small text-muted">
                            {field.position ? (
                              <>
                                <div>Page {field.position.page}</div>
                                <div className="font-monospace" style={{ fontSize: '0.75rem' }}>
                                  x: {(field.position.x * 100).toFixed(1)}%
                                  <br />
                                  y: {(field.position.y * 100).toFixed(1)}%
                                </div>
                              </>
                            ) : (
                              <span>—</span>
                            )}
                          </td>
                          <td>
                            {field.nearbyFields.length > 0 ? (
                              <div className="d-flex flex-wrap gap-1">
                                {field.nearbyFields.map((nf, i) => (
                                  <code key={i} className="small text-muted">{nf}</code>
                                ))}
                              </div>
                            ) : (
                              <span className="text-muted small">No nearby named fields on same page</span>
                            )}
                          </td>
                          {aiSuggestions && (
                            <td>
                              {suggestion ? (
                                <>
                                  <code className="text-success">{suggestion.suggestedName}</code>
                                  {suggestion.confidence && (
                                    <Badge 
                                      bg={suggestion.confidence === 'high' ? 'success' : suggestion.confidence === 'medium' ? 'warning' : 'secondary'}
                                      className="ms-2 small"
                                    >
                                      {suggestion.confidence}
                                    </Badge>
                                  )}
                                  {suggestion.reason && (
                                    <div className="text-muted small">{suggestion.reason}</div>
                                  )}
                                </>
                              ) : (
                                <span className="text-muted">—</span>
                              )}
                            </td>
                          )}
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
                
                {aiSuggestions && (
                  <Alert variant="info" className="mt-3 mb-0">
                    <FontAwesomeIcon icon={faExternalLinkAlt} className="me-2" />
                    To apply these changes, open the DocuSeal template editor and rename the fields manually.
                    Then click "Refresh" to pull the updated template.
                  </Alert>
                )}
              </>
            )}
          </Card.Body>
        </Card>
      </Panel>
      
      {/* Confirmation Modal for Examiner Field Fixes */}
      <Modal show={showConfirmModal} onHide={() => setShowConfirmModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Confirm Examiner Field Updates</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Alert variant="info">
            <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
            The following field names will be updated in both the DocuSeal template and the form data to match the standard examiner field names.
          </Alert>
          <Table responsive size="sm">
            <thead>
              <tr>
                <th>Current Name</th>
                <th></th>
                <th>Standard Name</th>
              </tr>
            </thead>
            <tbody>
              {examinerChanges.map((change, idx) => (
                <tr key={idx}>
                  <td><code className="text-danger">{change.from}</code></td>
                  <td className="text-center">→</td>
                  <td>
                    <code className="text-success">{change.to}</code>
                    <div className="text-muted small">{change.label}</div>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Modal.Body>
        <Modal.Footer>
          <Button 
            variant="secondary" 
            onClick={() => setShowConfirmModal(false)}
            disabled={fixExaminerFieldsMutation.isPending}
          >
            Cancel
          </Button>
          <Button 
            variant="primary" 
            onClick={handleConfirmFix}
            disabled={fixExaminerFieldsMutation.isPending}
          >
            {fixExaminerFieldsMutation.isPending ? (
              <>
                <FontAwesomeIcon icon={faSpinner} spin className="me-2" />
                Updating...
              </>
            ) : (
              <>
                <FontAwesomeIcon icon={faSave} className="me-2" />
                Apply Changes
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </PrivateLayout>
  );
};

export default DbqTemplateDebug;
